import { useEffect } from 'react';
import { List } from 'immutable';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveMeetingChatSessionId,
    selectMeetingIsActive,
} from 'flow/selectors/meeting-selectors';
import { selectVideoChatExternalMessages } from 'flow/selectors/video-chat-selectors';
import { setVideoChatExternalMessagesViewingAction } from 'flow/actions/video-chat-actions';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { VideoChatMessage } from 'flow/types/VideoChat';

/**
 * Returns the external (guest) messages from the in-meeting video-chat
 * side-channel, but ONLY when this MessageList is rendering the active
 * meeting's chat session — otherwise undefined. Lets the merged in-meeting
 * panel interleave guest messages while every other chat (and the same chat
 * outside a meeting) stays purely chat-service.
 *
 * `selectActiveMeetingChatSessionId` holds the raw id; ChatSessionKey
 * consumers use the `chatsession-{id}` form, so we prefix to compare.
 *
 * The relay messages are read inside this single selector ONLY for the
 * meeting's own session — every other MessageList returns a stable `undefined`,
 * so they don't subscribe to relay state and don't re-render on each incoming
 * relay message during a meeting.
 *
 * Also reports itself as "viewing" to the video-chat slice (VID-433).
 */
export const useInMeetingExternalMessages = (
    chatSessionKey: ChatSessionKey,
): List<TypedMap<VideoChatMessage>> | undefined => {
    const dispatch = useAppDispatch();

    const isActiveMeetingSession = useAppSelector((state) => {
        if (!selectMeetingIsActive(state)) {
            return false;
        }
        const rawId = selectActiveMeetingChatSessionId(state);
        const activeMeetingChatSessionKey = rawId
            ? `chatsession-${rawId}`
            : null;
        return chatSessionKey === activeMeetingChatSessionKey;
    });

    useEffect(() => {
        // Only touch the shared flag for the instance actually viewing the meeting's
        // session — otherwise an unrelated MessageList mounting/unmounting elsewhere
        // would clobber it back to false while the meeting panel is still open.
        if (!isActiveMeetingSession) {
            return undefined;
        }
        dispatch(setVideoChatExternalMessagesViewingAction(true));
        return () => {
            dispatch(setVideoChatExternalMessagesViewingAction(false));
        };
    }, [dispatch, isActiveMeetingSession]);

    return useAppSelector((state) =>
        isActiveMeetingSession
            ? selectVideoChatExternalMessages(state)
            : undefined,
    );
};
